import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { useAuth } from '../App';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { formatApiError } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, no-token
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage('No verification token provided.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.post(`${API}/api/auth/verify-email`, { token }, {
          withCredentials: true
        });
        
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        
        // Auto-login the user
        if (response.data.token && response.data.user) {
          loginWithToken(response.data.token, response.data.user);
          toast.success('Email verified! Redirecting...');
          
          // Redirect after a short delay
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      } catch (error) {
        setStatus('error');
        setMessage(formatApiError(error.response?.data?.detail, 'Failed to verify email. The link may be invalid or expired.'));
      }
    };

    verifyEmail();
  }, [token, loginWithToken, navigate]);

  const handleResendVerification = async () => {
    if (!resendEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, { email: resendEmail }, {
        withCredentials: true
      });
      toast.success('A new verification link has been sent to your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card rounded-2xl p-8 max-w-md w-full shadow-lg text-center" data-testid="verify-email-page">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Verifying Email...</h1>
            <p className="text-muted-foreground">Please wait while we verify your email address.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Email Verified!</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <p className="text-sm text-muted-foreground">Redirecting to homepage...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Verification Failed</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            
            <div className="border-t border-border pt-6 mt-6">
              <h3 className="text-lg font-medium text-foreground mb-3">Request New Verification Link</h3>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="resend-email-input"
                />
                <Button 
                  onClick={handleResendVerification} 
                  disabled={resending}
                  className="w-full"
                  data-testid="resend-verification-btn"
                >
                  {resending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Resend Verification Email</>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {status === 'no-token' && (
          <>
            <Mail className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Email Verification</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            
            <div className="border-t border-border pt-6 mt-6">
              <h3 className="text-lg font-medium text-foreground mb-3">Need a new verification link?</h3>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="resend-email-input"
                />
                <Button 
                  onClick={handleResendVerification} 
                  disabled={resending}
                  className="w-full"
                  data-testid="resend-verification-btn"
                >
                  {resending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Send Verification Email</>
                  )}
                </Button>
              </div>
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="mt-4 w-full"
              data-testid="go-home-btn"
            >
              Go to Homepage
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
